import stationDataConfigApi from '@/api/config/stationDataConfig'
import { queryKeys } from '@/constant/common'
import { NotifyService } from '@/helpers'
import { getValidationSummary } from '@/helpers/apiError'
import { UpdateStation } from '@/types/station'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useConfigUI } from '../../context'
import useTranslation from '@/hooks/useTranslation'

async function updateStation(payload: UpdateStation): Promise<void> {
  await stationDataConfigApi.update(payload)
}

/**
 * `quietCache` is for a bulk run (SCRUM-1010 QC items 1 and 2). Patching every
 * cached station page and invalidating the list **per row** turned a 491-row
 * save into ~4,000 list reads — measured at 8 reads per row written — which is
 * most of the two minutes QC saw, and the churn the spinner stutters under. The
 * caller refreshes once when the run ends instead.
 *
 * `showError` off lets that caller report the failures per row, rather than one
 * toast per failed station on top of its summary.
 */
function useUpdateStation({ showNoti = true, quietCache = false, showError = true } = {}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // Undefined outside the config sidebar provider (e.g. the Map View reuses the
  // station grid) — closing the sidebar is a no-op there.
  const configUI = useConfigUI()

  return useMutation((payload: UpdateStation) => updateStation(payload), {
    onSuccess: (_, variables) => {
      if (quietCache) return
      // Patch the cache immediately so the table reflects the change before the background refetch
      const allStationQueries = queryClient.getQueriesData<any>([queryKeys.stations])
      allStationQueries.forEach(([queryKey, queryData]) => {
        if (!queryData?.pages) return
        queryClient.setQueryData(queryKey, {
          ...queryData,
          pages: queryData.pages.map((page: any) => ({
            ...page,
            list: (page.list || []).map((station: any) =>
              station.id === variables.id
                ? {
                    ...station,
                    precipitationRate: variables.precipitationRate,
                    expectedFlow: variables.expectedFlow,
                    waterRequired: variables.waterRequired,
                    defaultRunTime: variables.defaultRunTime,
                    priority: variables.priority,
                    stationType: variables.Type,
                  }
                : station
            ),
          })),
        })
      })
      queryClient.invalidateQueries([queryKeys.stations])
      if (showNoti) {
        showNoti && NotifyService.success(t('UpdateStationSuccess'))
      }
      configUI?.closeSidebar?.()
    },
    onError: (error: any) => {
      if (!showError) return
      toast.error(getValidationSummary(error, t('UpdateStationError')))
      // NotifyService.error(error)
    },
  })
}

export default useUpdateStation
